package org.gymly.ui.admin

import org.gymly.model.AdminExchangeRateQuote
import org.gymly.model.AdminPaymentCollectLine
import org.gymly.model.AdminPaymentMethod
import org.gymly.util.GymlyCurrency
import org.gymly.util.formatMoney

/**
 * Garantías de cobro masivo: cobertura por persona, móvil compartido (suma + alerta, sin bloquear),
 * vuelto una sola vez, pendiente Bs/$ por cliente.
 */

/** Monto Bs a cobrar ahora: plan − pendiente Bs explícito. */
fun bulkDueBsCents(amountCents: Long, pendingBsCents: Long): Long =
    (amountCents - pendingBsCents.coerceAtLeast(0L)).coerceAtLeast(0L)

/** Lo que paga la persona en Bs después de su descuento (plan − pendiente − descuento). */
fun bulkNetDueBsCents(amountCents: Long, pendingBsCents: Long, discountCents: Long): Long =
    (bulkDueBsCents(amountCents, pendingBsCents) - discountCents.coerceAtLeast(0L)).coerceAtLeast(0L)

/** Vuelto del cobro combinado: solo el primero indicado (no sumar filas). */
fun bulkSingleChangeGivenCents(values: List<Long>): Long =
    values.firstOrNull { it > 0L }?.coerceAtLeast(0L) ?: 0L

fun bulkSingleChangeGivenUsdCents(values: List<Long>): Long =
    values.firstOrNull { it > 0L }?.coerceAtLeast(0L) ?: 0L

/**
 * Prepara líneas por fila (monto en blanco → monto de la fila) y fusiona móvil compartido sumando.
 */
fun prepareBulkCollectLines(
    entries: List<Pair<String, List<PaymentLineDraft>>>,
    fallbackAmountInput: String,
    quote: AdminExchangeRateQuote?,
): Pair<List<PaymentLineDraft>, List<AdminPaymentCollectLine>> {
    val flat = entries.flatMap { (amountInput, lines) ->
        lines.map { line ->
            val filledRaw = line.amountInput.ifBlank { amountInput }
            val filled = if (parseBulkAmountToCents(filledRaw) <= 0L && amountInput.isNotBlank()) {
                amountInput
            } else {
                filledRaw
            }
            line.copy(
                amountInput = filled,
                notes = normalizePaymentReference(line.method, line.notes),
                payerPhone = "",
            )
        }
    }
    val merged = mergeSharedPaymentLines(flat)
    val collectLines = merged.map { line ->
        line.toCollectLine(
            line.amountInput.ifBlank { fallbackAmountInput },
            quote,
        )
    }
    return merged to collectLines
}

/**
 * Alerta informativa (no bloquea): refs de pago móvil usadas por más de una persona.
 */
fun bulkSharedMobileRefAlerts(
    people: List<Pair<String, List<PaymentLineDraft>>>,
): List<String> {
    val refToNames = linkedMapOf<String, MutableList<String>>()
    for ((name, lines) in people) {
        for (line in lines) {
            if (line.method != AdminPaymentMethod.MOBILE_PAYMENT) continue
            val ref = normalizePaymentReference(line.method, line.notes)
            if (ref.length < 5) continue
            refToNames.getOrPut(ref) { mutableListOf() }.add(name)
        }
    }
    return refToNames
        .filter { it.value.distinct().size > 1 }
        .map { (ref, names) ->
            "Pago móvil ref. $ref compartido por ${names.distinct().joinToString(", ")} (un solo cobro, sin bloquear)."
        }
}

/**
 * Valida que las líneas cubran la suma de dues Bs, o que haya cobro en $ si Bs no alcanza.
 * @return mensaje de error o null si OK.
 */
fun validateBulkCollectCoverage(
    dueBsTotal: Long,
    collectLines: List<AdminPaymentCollectLine>,
): String? {
    if (dueBsTotal <= 0L) {
        val hasUsd = collectLines.any { (it.foreignUsdCents ?: 0L) > 0L }
        val hasBs = collectLines.any { it.amountCents > 0L }
        if (!hasUsd && !hasBs) {
            return "Indica montos de cobro en Bs o en $ para los socios de la planilla."
        }
        return null
    }
    val lineBs = collectLines.sumOf { it.amountCents.coerceAtLeast(0L) }
    val lineUsd = collectLines.sumOf { (it.foreignUsdCents ?: 0L).coerceAtLeast(0L) }
    if (lineBs < dueBsTotal && lineUsd <= 0L) {
        return "Los montos de cobro (${formatMoney(lineBs.toInt(), GymlyCurrency.CODE)}) " +
            "no cubren a todos los socios (${formatMoney(dueBsTotal.toInt(), GymlyCurrency.CODE)}). " +
            "Revisa cada persona, suma el móvil compartido o indica pendiente Bs/$."
    }
    return null
}
